// R52 — HER CIRCLE, AND THE ONE FORM THAT ADDS TO IT READ NOTHING BUT SUCCESS.
//
// THE LIVE DEFECT THIS FILE EXISTS TO CLOSE, and it is R26/R47's shape once more: the capability
// lives only on the surface that needed it first.
//
// I6 RULE 3 (owner, 2026-07-16) says the server refuses a silent duplicate and hands back the person
// the member already has, **so the save path can ask warmly instead of failing quietly**. Two client
// surfaces POST `/api/people`:
//
//   * `savePersonFromChart` — the "Someone new" offer after a reading. It reads `data.duplicate`,
//     raises `DuplicatePersonPrompt`, and Rule 3 works exactly as written.
//   * `savePerson` — **MY ROOM'S OWN PEOPLE FORM, the front door of that whole feature.** It read
//     `if (data.ok && data.person)` and had NO ELSE. So adding someone already in her circle did
//     nothing at all: no prompt, no sentence, the form still open with her typed details and the
//     Save button un-greyed. She retypes it and taps again, forever. **AN ABSENT CONTROL HAS NO
//     ERROR STATE** (R25) — she cannot tell a refusal from a button that is broken.
//
// The same `else` swallowed every other answer the route can give: **401** "Sign in to save people
// to your room."; **400** "A name is required."; **500** "People of interest is unavailable right
// now."; and **HTTP 200 `{ people: [] }`** — the `ensureUser` early return, which fires BEFORE the
// method branch, so a POST to an unreachable database is answered with an empty LIST. That payload
// carries no `ok` and no `message` at all.
//
// So the transport lives here ONCE and hands back an ANSWER (R31: the crossing stops existing).
// `saved-readings.ts` and `room-state.ts` are the same file on other routes and the answer shape is
// deliberately theirs — **plus one kind those two do not have, because this route has a fifth thing
// to say: `duplicate` is not a refusal, it is a QUESTION**, and collapsing it into "refused" is what
// made the My Room form silent.
//
// This module imports NOTHING but `import type` (the entry requirement for `scripts/`-loadable law).

export const peoplePath = "/api/people";
export const personPath = (id: string) => `${peoplePath}/${encodeURIComponent(id)}`;
export const personChartPath = (id: string) => `${personPath(id)}/chart`;

// FIVE answers. The middle three are the ones a status code cannot tell apart, and the fifth is the
// one this route alone has.
//   duplicate — I6 Rule 3: she already has this person. Not an error; a question, with the answer
//               she needs in order to answer it.
//   sign-in   — the session is gone (401). Nothing of hers is lost.
//   refused   — the server DELIBERATELY said no, at 200 or at 4xx/5xx, and usually said why.
//   offline   — the request never landed. Nothing was decided; transport noise (R18).
export type PersonAnswer<T> =
  | { ok: true; person: T }
  | { ok: false; kind: "duplicate"; existing: T }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

// The two sentences the house owns, handed in by whoever holds the string catalog — this module
// imports nothing and cannot read it.
export type PersonCopy = { signIn: string; failed: string };

type Json = { ok?: boolean; message?: string; person?: unknown; duplicate?: unknown };

// `doFetch` is a PARAMETER (R46's locked-door shape) so a gate drives every answer the route can
// give without a browser, a session or a database.
async function ask<T>(path: string, init: RequestInit, doFetch: typeof fetch): Promise<PersonAnswer<T>> {
  try {
    const res = await doFetch(path, init);
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = (await res.json().catch(() => ({}))) as Json;
    // **THE QUESTION IS ASKED BEFORE THE REFUSAL**, because the route sends it AS a refusal
    // (`200 { ok: false, duplicate }`). Testing `ok === false` first would file Rule 3's whole
    // mechanism under "the server said no" and there would be nothing to prompt with.
    if (data?.duplicate) return { ok: false, kind: "duplicate", existing: data.duplicate as T };
    if (res.ok && data?.ok && data.person) return { ok: true, person: data.person as T };
    return { ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

const jsonPost = (method: string, body: unknown) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

export function addPerson<T>(body: unknown, doFetch: typeof fetch = fetch): Promise<PersonAnswer<T>> {
  return ask<T>(peoplePath, jsonPost("POST", body), doFetch);
}

export function editPerson<T>(id: string, body: unknown, doFetch: typeof fetch = fetch): Promise<PersonAnswer<T>> {
  return ask<T>(personPath(id), jsonPost("PATCH", body), doFetch);
}

// THE READ. Four effects fetched this list and each wrote its own `r.ok ? r.json() : { people: [] }`.
// **A FAILED READ IS NOT AN EMPTY CIRCLE** (R18): it answers null, so the surface keeps whatever it
// was already showing rather than blanking her people because a request lost its way. The route's
// own `ensureUser` early return sends a real `{ people: [] }`, and that IS an answer — an account
// with no rows — so the two are told apart by the shape, not by the status code.
export async function listPeople<T>(doFetch: typeof fetch = fetch): Promise<T[] | null> {
  try {
    const res = await doFetch(peoplePath);
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return null; }
    const data = (await res.json().catch(() => null)) as { people?: unknown } | null;
    return Array.isArray(data?.people) ? (data.people as T[]) : null;
  } catch {
    return null;
  }
}

// THE DELETE, and it is R35's finding from the other end. The client already honours this answer
// (a card comes off her screen only if the server really took the row); until this round the server
// answered `{ ok: true }` whether or not anything was deleted, including on a database it could not
// reach. TRUE only if the server said the row is gone.
export async function deletePerson(id: string, doFetch: typeof fetch = fetch): Promise<boolean> {
  try {
    const res = await doFetch(personPath(id), { method: "DELETE" });
    const data = (await res.json().catch(() => ({}))) as Json;
    return res.ok && data?.ok !== false;
  } catch {
    return false;
  }
}

// The sentence, derived from the answer rather than typed per surface. A DUPLICATE gets no sentence
// at all — the surface raises the prompt instead, and a refusal line under a question she is being
// asked reads as a failure she has to fix.
export function personNote<T>(answer: PersonAnswer<T>, copy: PersonCopy): string {
  if (answer.ok || answer.kind === "duplicate") return "";
  if (answer.kind === "sign-in") return copy.signIn;
  if (answer.kind === "refused" && answer.message) return answer.message;
  return copy.failed;
}
